import { useAuth } from "@clerk/clerk-react";
import { Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Map } from "schemas/functions";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import LocationAutocomplete, {
  LocationAutocompleteHandle,
} from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledTextField from "components/Styled/TextField";
import StyledVaPage from "components/Styled/VAPage";
import OnboardingSettings from "features/Influencer/VA/VASettings/GeneralSettings/OnboardingSettings";
import { fetcherAuth } from "utils/api";
import { getSource } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useTracking } from "utils/useTracking";

import styles from "./styles";

interface Props {
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  nichesFetched: boolean;
  finishSetUpLater: () => void;
}

const SelfServeAboutPage = ({
  onboardingForm,
  setOnboardingForm,
  nichesFetched,
  finishSetUpLater,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { trackVaStep } = useTracking();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    currentOrg,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const [
    localIncludedBentoSearchFilterIds,
    setLocalIncludedBentoSearchFilterIds,
  ] = useState<number[]>(includedBentoSearchFilterIds);
  const [
    localExcludedBentoSearchFilterIds,
    setLocalExcludedBentoSearchFilterIds,
  ] = useState<number[]>(excludedBentoSearchFilterIds);
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(-1);

  const [error, setError] = useState<string>("");
  const locationAutocompleteRef = useRef<LocationAutocompleteHandle>(null);

  const handleValidateForm = (form: Map) => {
    if (!form?.addressComponents || form?.addressComponents.length < 2) {
      setError("Please fill in a city or zip code");
      setActiveQuestionIndex(1);
      return false;
    }

    if (!form?.city) {
      const neighborhood = form.addressComponents.find(
        (component: any) =>
          component.types.includes("sublocality") ||
          component.types.includes("neighborhood"),
      )?.longText;
      if (neighborhood) {
        form.city = neighborhood;
      } else {
        form.city = form.addressComponents[0].longText;
      }
    }

    if (!form?.location) {
      setActiveQuestionIndex(1);
      setError(
        "We could not extract the country from your location, please input the full address like `Kosovo Polje, Bosnia and Herzegovina`",
      );
      return false;
    }

    if (!form?.profileTypes || form?.profileTypes.length === 0) {
      setActiveQuestionIndex(2);
      setError("Please fill in who are you");
      return false;
    }
    return true;
  };

  const handleChange = (e: CustomEvent) => {
    setIsEdited(true);
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setIsEdited(true);
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const handleSubmitAbout = async () => {
    let formToSubmit: Map = onboardingForm;
    // Always re-resolve against the input text — state may be stale if the
    // user edited the prefilled text without fully clearing it.
    const result = await locationAutocompleteRef.current?.resolveLocation();
    if (result?.resolved?.country_code) {
      formToSubmit = {
        ...formToSubmit,
        city: result.resolved.address,
        location: result.resolved.country_code,
        addressComponents: result.resolved.address_components,
        latitude: result.resolved.latitude,
        longitude: result.resolved.longitude,
      };
      setOnboardingForm(formToSubmit);
      setIsEdited(true);
    } else if (result?.hasText && !result?.valid) {
      setActiveQuestionIndex(1);
      setError("Please select a location from the dropdown suggestions");
      return;
    }

    const isValid = handleValidateForm(formToSubmit);
    if (!isValid) return;

    if (!isEdited) {
      navigate(`/${routes.nicheSelfServeOnboarding}`);
      return;
    }

    trackVaStep(routes.nicheSelfServeOnboarding);
    navigate(`/${routes.nicheSelfServeOnboarding}`);

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...formToSubmit,
          page: location.pathname,
          source: getSource(),
          isVaFlow: true,
        },
      );
      await updateFilters();
    } catch (error) {
      setErrorAlert(error);
      navigate(`/${routes.aboutSelfServeOnboarding}`);
    }
  };

  const updateFilters = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/va-settings`,
        "PUT",
        {},
        {
          profileTypes: onboardingForm.profileTypes,
          excludeBentoSearchFilterIds: localExcludedBentoSearchFilterIds,
          includeBentoSearchFilterIds: localIncludedBentoSearchFilterIds,
        },
      );
      setIncludedBentoSearchFilterIds(localIncludedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(localExcludedBentoSearchFilterIds);
    } catch (error) {
      setErrorAlert(error);
      navigate(`/${routes.aboutSelfServeOnboarding}`);
    }
  };

  const setActiveQuestionAutomatically = () => {
    if (!onboardingForm?.name) {
      setActiveQuestionIndex(0);
      return;
    }
    const locationCompleted =
      !!onboardingForm?.addressComponents &&
      onboardingForm?.addressComponents.length >= 2 &&
      !!onboardingForm?.location;
    if (!locationCompleted) {
      setActiveQuestionIndex(1);
      return;
    }
    if (
      !onboardingForm?.profileTypes ||
      onboardingForm?.profileTypes.length === 0
    ) {
      setActiveQuestionIndex(2);
      return;
    }
    if (localIncludedBentoSearchFilterIds.length === 0) {
      setActiveQuestionIndex(3);
      return;
    }
    if (localExcludedBentoSearchFilterIds.length === 0) {
      setActiveQuestionIndex(4);
      return;
    }
    setActiveQuestionIndex(-1);
  };

  useEffect(() => {
    setActiveQuestionAutomatically();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(onboardingForm?.name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(onboardingForm?.addressComponents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(
      onboardingForm?.profileTypes && onboardingForm?.profileTypes.length > 0,
    ),
  ]);

  return (
    <StyledVaPage
      stepNumber={1}
      splitScreen
      title="Let's get to know you"
      description="The more we know about you, the smarter your brand matches will be 🔥"
      isInsideForm={true}
      time="2-3"
      body={
        <Grid container justifyContent="center">
          <Grid item xs={12} sx={styles.fieldContainer}>
            <StyledTextField
              fullWidth
              name="name"
              placeholder="First Last"
              onChange={handleChange}
              required
              value={onboardingForm?.name || ""}
              completed={!!onboardingForm?.name}
              label="Your Name"
              allowCollapsible
              isActive={activeQuestionIndex === 0}
              error={error}
            />
          </Grid>
          <Grid item xs={12} sx={styles.fieldContainer}>
            <LocationAutocomplete
              ref={locationAutocompleteRef}
              handleLocationChange={handleLocationChange}
              userLocation={{
                address: onboardingForm.city,
                longitude: onboardingForm.longitude,
                latitude: onboardingForm.latitude,
                country_code: onboardingForm.location,
                address_components: onboardingForm.addressComponents,
              }}
              customStyling={{
                height: 52,
                width: "100%",
                borderRadius: 4,
                border: `1px solid ${grey[300]}`,
                px: 1,

                "&:hover, &:focus": {
                  outline: "none",
                },
              }}
              label="City or Zip Code"
              required
              allowCollapsible
              completed={
                !!onboardingForm?.addressComponents &&
                onboardingForm?.addressComponents.length >= 2 &&
                !!onboardingForm?.location
              }
              isActive={activeQuestionIndex === 1}
            />

            {error && activeQuestionIndex === 1 && (
              <Typography sx={{ color: "error.main" }}>{error}</Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <OnboardingSettings
              // @ts-ignore
              profile={onboardingForm}
              // @ts-ignore
              setProfile={setOnboardingForm}
              includedBentoSearchFilterIds={localIncludedBentoSearchFilterIds}
              excludedBentoSearchFilterIds={localExcludedBentoSearchFilterIds}
              setIncludedBentoSearchFilterIds={
                setLocalIncludedBentoSearchFilterIds
              }
              setIsEdited={setIsEdited}
              setExcludedBentoSearchFilterIds={
                setLocalExcludedBentoSearchFilterIds
              }
              activeQuestionIndex={activeQuestionIndex}
              setActiveQuestionIndex={setActiveQuestionIndex}
              error={error}
            />
          </Grid>
        </Grid>
      }
      buttonNext={"Continue to Niches"}
      onNext={handleSubmitAbout}
      buttonBack="Back"
      onFinishSetUpLater={finishSetUpLater}
      onBack={() => {
        trackVaStep(routes.welcomeSelfServeOnboarding);
        navigate(`/${routes.welcomeSelfServeOnboarding}`);
      }}
      loading={!nichesFetched}
    />
  );
};

export default SelfServeAboutPage;
